import { memo, useState } from "react"
import { BsThreeDotsVertical } from "react-icons/bs"
import { FaEdit } from "react-icons/fa"
import { FiDollarSign, FiTrash2 } from "react-icons/fi"

import { Field } from "@/components/atoms/Field"
import { toaster } from "@/components/infrastructure"
import { useDeleteStudent, useUpdateStudent } from "@/hooks/api/useStudents"
import { useSchoolSettings } from "@/hooks/api/useSchoolSettings"
import type { StudentResponse, StudentUpdateInput } from "@/types/student"
import { studentUpdateSchema } from "@/types/student"
import { Link as RouterLink } from "@tanstack/react-router"

import {
  Box,
  Button,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Controller,
  type Resolver,
  type SubmitHandler,
  useForm,
} from "react-hook-form"

interface StudentActionsMenuProps {
  student: StudentResponse
}

export const StudentActionsMenu = memo(
  ({ student }: StudentActionsMenuProps) => {
    const [showEditDialog, setShowEditDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const { swimLevels, locations } = useSchoolSettings()

    const deleteStudentMutation = useDeleteStudent()
    const updateStudentMutation = useUpdateStudent()

    const form = useForm<StudentUpdateInput>({
      resolver: zodResolver(
        studentUpdateSchema,
      ) as Resolver<StudentUpdateInput>,
      mode: "onBlur",
      defaultValues: {
        first_name: student.first_name ?? "",
        last_name: student.last_name ?? "",
        date_of_birth: student.date_of_birth ?? "",
        gender: student.gender ?? null,
        household_id: student.household_id ?? "",
        current_swim_level: student.current_swim_level ?? null,
        skill_level_notes: student.skill_level_notes ?? "",
        has_asthma: student.has_asthma ?? false,
        preferred_location: student.preferred_location ?? null,
        notes: student.notes ?? "",
        is_active: student.is_active ?? false,
        photo_url: student.photo_url ?? "",
      },
    })

    const getErrorText = (error: unknown): string | undefined => {
      if (typeof error === "object" && error !== null) {
        const maybeMessage = (error as { message?: unknown }).message
        if (typeof maybeMessage === "string") {
          return maybeMessage
        }
      }
      return undefined
    }

    const displayLabel = String(student.first_name ?? student.id)

    const handleDelete = async () => {
      try {
        await deleteStudentMutation.mutateAsync(student.id)
        setShowDeleteDialog(false)
        toaster.create({
          title: "Student deleted",
          description: `${displayLabel} has been deleted successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error deleting student:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to delete student"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to delete this student"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Delete failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleUpdate: SubmitHandler<StudentUpdateInput> = async (data) => {
      try {
        await updateStudentMutation.mutateAsync({
          studentId: student.id,
          studentData: data,
        })
        setShowEditDialog(false)
        form.reset({
          first_name: student.first_name ?? "",
          last_name: student.last_name ?? "",
          date_of_birth: student.date_of_birth ?? "",
          gender: student.gender ?? null,
          household_id: student.household_id ?? "",
          current_swim_level: student.current_swim_level ?? null,
          skill_level_notes: student.skill_level_notes ?? "",
          has_asthma: student.has_asthma ?? false,
          preferred_location: student.preferred_location ?? null,
          notes: student.notes ?? "",
          is_active: student.is_active ?? false,
          photo_url: student.photo_url ?? "",
        })
        toaster.create({
          title: "Student updated",
          description: `${displayLabel} has been updated successfully`,
          type: "success",
        })
      } catch (error: any) {
        console.error("Error updating student:", error)

        // Show user-friendly error message
        let errorMessage = "Failed to update student"
        if (
          error?.message?.includes("Insufficient permissions") ||
          error?.status === 403
        ) {
          errorMessage = "You don't have permission to edit this student"
        } else if (error?.message) {
          errorMessage = error.message
        }

        toaster.create({
          title: "Update failed",
          description: errorMessage,
          type: "error",
        })
      }
    }

    const handleEditClose = () => {
      setShowEditDialog(false)
      form.reset({
        first_name: student.first_name ?? "",
        last_name: student.last_name ?? "",
        date_of_birth: student.date_of_birth ?? "",
        gender: student.gender ?? null,
        household_id: student.household_id ?? "",
        current_swim_level: student.current_swim_level ?? null,
        skill_level_notes: student.skill_level_notes ?? "",
        has_asthma: student.has_asthma ?? false,
        preferred_location: student.preferred_location ?? null,
        notes: student.notes ?? "",
        is_active: student.is_active ?? false,
        photo_url: student.photo_url ?? "",
      })
    }

    return (
      <>
        <MenuRoot>
          <MenuTrigger asChild>
            <IconButton
              variant="ghost"
              color="inherit"
              aria-label={`Actions for ${displayLabel}`}
            >
              <BsThreeDotsVertical />
            </IconButton>
          </MenuTrigger>
          <MenuContent>
            <RouterLink to="/students/$studentId/account" params={{ studentId: student.id }}>
              <MenuItem value="account">
                <FiDollarSign fontSize="14px" aria-hidden="true" />
                View Account
              </MenuItem>
            </RouterLink>
            <MenuItem value="edit" onClick={() => setShowEditDialog(true)}>
              <FaEdit fontSize="14px" aria-hidden="true" />
              Edit
            </MenuItem>
            <MenuItem value="delete" onClick={() => setShowDeleteDialog(true)}>
              <FiTrash2 fontSize="14px" aria-hidden="true" />
              Delete
            </MenuItem>
          </MenuContent>
        </MenuRoot>

        {/* Edit Dialog */}
        <DialogRoot
          open={showEditDialog}
          onOpenChange={(e) => !e.open && handleEditClose()}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent as="form" onSubmit={form.handleSubmit(handleUpdate)}>
            <DialogHeader>
              <DialogTitle>Edit Student</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <VStack gap={6}>
                <Field
                  invalid={!!form.formState.errors.first_name}
                  errorText={getErrorText(form.formState.errors.first_name)}
                  label="First Name"
                >
                  <Input
                    placeholder={"Enter first name"}
                    aria-invalid={!!form.formState.errors.first_name}
                    {...form.register("first_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.last_name}
                  errorText={getErrorText(form.formState.errors.last_name)}
                  label="Last Name"
                >
                  <Input
                    placeholder={"Enter last name"}
                    aria-invalid={!!form.formState.errors.last_name}
                    {...form.register("last_name")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.date_of_birth}
                  errorText={getErrorText(form.formState.errors.date_of_birth)}
                  label="Date Of Birth"
                >
                  <Input
                    type="date"
                    placeholder={"Select date of birth"}
                    aria-invalid={!!form.formState.errors.date_of_birth}
                    {...form.register("date_of_birth")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.gender}
                  errorText={getErrorText(form.formState.errors.gender)}
                  label="Gender"
                >
                  <select
                    {...form.register("gender", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.gender}
                    className="chakra-select"
                  >
                    {" "}
                    <option value="">Select Gender</option>{" "}
                    <option value="male">Male</option>{" "}
                    <option value="female">Female</option>{" "}
                    <option value="other">Other</option>{" "}
                    <option value="prefer_not_to_say">
                      Prefer Not To Say
                    </option>{" "}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.household_id}
                  errorText={getErrorText(form.formState.errors.household_id)}
                  label="Household Id"
                >
                  <Input
                    placeholder={"Enter household id"}
                    aria-invalid={!!form.formState.errors.household_id}
                    {...form.register("household_id")}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.current_swim_level}
                  errorText={getErrorText(
                    form.formState.errors.current_swim_level,
                  )}
                  label="Current Swim Level"
                >
                  <select
                    {...form.register("current_swim_level", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.current_swim_level}
                    className="chakra-select"
                  >
                    <option value="">Select Current Swim Level</option>
                    {swimLevels.map((sl) => (
                      <option key={sl.value} value={sl.value}>{sl.label}</option>
                    ))}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.skill_level_notes}
                  errorText={getErrorText(
                    form.formState.errors.skill_level_notes,
                  )}
                  label="Skill Level Notes"
                >
                  <Textarea
                    {...form.register("skill_level_notes")}
                    placeholder={"Enter skill level notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.skill_level_notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.has_asthma}
                  errorText={getErrorText(form.formState.errors.has_asthma)}
                  label="Has Asthma"
                >
                  <Controller
                    name="has_asthma"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="has_asthma"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Has Asthma"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.preferred_location}
                  errorText={getErrorText(
                    form.formState.errors.preferred_location,
                  )}
                  label="Preferred Location"
                >
                  <select
                    {...form.register("preferred_location", {
                      setValueAs: (value) => (value === "" ? null : value),
                    })}
                    aria-invalid={!!form.formState.errors.preferred_location}
                    className="chakra-select"
                  >
                    <option value="">Select Preferred Location</option>
                    {locations.map((loc) => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.notes}
                  errorText={getErrorText(form.formState.errors.notes)}
                  label="Notes"
                >
                  <Textarea
                    {...form.register("notes")}
                    placeholder={"Enter notes"}
                    rows={3}
                    resize="vertical"
                    aria-invalid={!!form.formState.errors.notes}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.is_active}
                  errorText={getErrorText(form.formState.errors.is_active)}
                  label="Is Active"
                >
                  <Controller
                    name="is_active"
                    control={form.control}
                    render={({ field: controllerField }) => (
                      <Switch.Root
                        id="is_active"
                        checked={!!controllerField.value}
                        onCheckedChange={({ checked }) =>
                          controllerField.onChange(checked)
                        }
                        aria-label="Is Active"
                      >
                        <Switch.HiddenInput
                          ref={controllerField.ref}
                          name={controllerField.name}
                          onBlur={controllerField.onBlur}
                        />
                        <Switch.Control>
                          <Switch.Thumb />
                        </Switch.Control>
                      </Switch.Root>
                    )}
                  />
                </Field>{" "}
                <Field
                  invalid={!!form.formState.errors.photo_url}
                  errorText={getErrorText(form.formState.errors.photo_url)}
                  label="Photo Url"
                >
                  <Input
                    placeholder={"Enter photo url"}
                    aria-invalid={!!form.formState.errors.photo_url}
                    {...form.register("photo_url")}
                  />
                </Field>{" "}
              </VStack>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={handleEditClose}
                  disabled={updateStudentMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorPalette="blue"
                  loading={updateStudentMutation.isPending}
                  loadingText="Updating..."
                >
                  Update Student
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>

        {/* Delete Dialog */}
        <DialogRoot
          open={showDeleteDialog}
          onOpenChange={(e) => !e.open && setShowDeleteDialog(false)}
          size="md"
        >
          <DialogBackdrop />
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Student</DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>

            <DialogBody>
              <Box
                bg="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="md"
                p={4}
                mb={4}
              >
                <Text color="yellow.800">
                  Are you sure you want to delete{" "}
                  <strong>"{displayLabel}"</strong>?
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.600">
                This action cannot be undone. The student will be permanently
                removed from the system.
              </Text>
            </DialogBody>

            <DialogFooter>
              <HStack gap={3}>
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={deleteStudentMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  colorPalette="red"
                  onClick={handleDelete}
                  disabled={deleteStudentMutation.isPending}
                >
                  {deleteStudentMutation.isPending
                    ? "Deleting..."
                    : "Delete Student"}
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      </>
    )
  },
)

StudentActionsMenu.displayName = "StudentActionsMenu"

export default StudentActionsMenu
